import { BasicSocialPost, SocialPost } from "types/collaboration";
import {
    SocialAccountApplicationItem,
    SocialAccountCollaborationItem,
} from "types/social-account";

export interface Influencer {
    uuid: string,
    name: string,
    age: number,
    gender_name: "Male" | "Female" | "Any",
    location?: string,
    ig_account: InfluencerIGAccount
    profile_picture_url?: string
    social_account: SocialAccount,
    mobile: string
}


export interface SocialAccount {
    uuid: string,
    name?: string,
    handle: string,
    platform: string,
    followers_count: number,
    profile_picture_url: string,
    engagement_score: string,
    engagement_rate: string,
    engagement_rate_details: DisplayData,
    authenticity_score?: string,
    authenticity_score_details?: DisplayData,
    brand_collaborations?: string[],
    niches?: string[],
    speciality_niches?: string[],
    speciality_niches_tier2?: string[],
    languages?: string[],
    language_names?: string[],
    ig?: IGSocialAccount,
    yt?: YTSocialAccount,
    location?: string,
}

export interface YTSocialAccount {
    title: string | null;
    username: string;
    video_count: number;
    subscriber_count: number;
    view_count: number;
    description: string;
    insights?: {
        demographic: SocialAccountDemographicInsights,
    },
    averages: {
        [key: string]: number
    },
    medians?: {
        [key: string]: number
    }
}

export interface IGSocialAccount {
    gender?: "M" | "F",
    age_bucket?: string,
    name: string,
    username: string,
    follows_count: number,
    followers_count: number,
    media_count: number,
    insights?: {
        demographic: SocialAccountDemographicInsights
    },
    averages: {
        [key: string]: number
    },
    medians?: {
        [key: string]: number
    }
}

export interface SocialAccountDemographicInsights {
    gender?: Record<string, any>,
    age?: Record<string, any>,
    country?: Record<string, any>,
    city?: Record<string, any>
}

interface DisplayData {
    name: string,
    color: string
}


interface DisplayData {
    name: string,
    color: string
}

export interface InfluencerIGAccount {
    followers_count: number,
    username?: string,
    media_count: number,
    profile_picture_url: string,
    engagement_score: string,
    engagement_rate: string,
    engagement_rate_details: DisplayData,
    authenticity_score: string,
    authenticity_score_details: DisplayData,
    quality_rating?: string,
    brand_collaborations?: string[],
    niches?: string[],
    languages?: string[],
    language_names?: string[]
}

export interface InsightItem {
    title: string;
    value: string | number;
    change: string | number;
}

export interface Insights {
    engagement: {
        impressions: InsightItem;
        accounts_engaged: InsightItem;
        profile_views: InsightItem;
        total_interactions: InsightItem;
        shares: InsightItem;
        likes: InsightItem;
        comments: InsightItem;
        replies: InsightItem;
        reach: InsightItem;
        follows: InsightItem;
        views?: InsightItem;
        plays?: InsightItem;
        saved?: InsightItem;
    };
    reach: {
        POST: InsightItem;
        REEL: InsightItem;
        STORY: InsightItem;
        CAROUSEL_CONTAINER: InsightItem;
    };
    total_interactions: {
        POST: InsightItem;
        REEL: InsightItem;
        STORY: InsightItem;
        CAROUSEL_CONTAINER: InsightItem;
    };
    follower_demographics: {
        country: any[];
        city: any[];
        age: any[];
        gender: any[];
    };
    engaged_audience_demographics: {
        country: any[];
        city: any[];
        age: any[];
        gender: any[];
    };
    reached_audience_demographics: {
        country: any[];
        city: any[];
        age: any[];
        gender: any[];
    };
    demographic_authenticity: any[];
}

interface Averages {
    comments: string,
    engagement_scores: string,
    likes: string,
    reach: string,
    views: string
}

export interface influencerSlice {
    loading: boolean,
    error: null | string,
    postLoading: boolean,
    postError: null|string
    insights: Record<string, {
        insights: Insights,
        averages: Averages,
        last_fetched_at: number
    }>
    posts: Record<string, Record<string, {post: Post, last_fetched_at: number}>>,
    social_posts: Record<string, Record<string, {post: SocialPost, last_fetched_at: number}>>,
    basic_posts: Record<string, {post: Post, last_fetched_at: number}>,
    media: Record<string, {
        [key in 'reels' | 'posts' | 'stories']: {
            items: Post[],
            last_fetched_at: number,
            expired: boolean,
            page: number,
            has_more: boolean,
            count: number,
            last_fetched_at_server: string
        }
    }>,
    social_media: Record<string, { // SocialAccountId
        [key: string]: SocialPosts 
    }>,
    basic_social_posts: Record<string, {post: BasicSocialPost, last_fetched_at: number}>,
    social_accounts: Record<string, {
        social_account: SocialAccount,
        last_fetched_at: number
    }>,
    social_account_applications: Record<string, {
        items: SocialAccountApplicationItem[],
        last_fetched_at: number,
        loading: boolean,
        error: string | null,
    }>,
    social_account_collaborations: Record<string, {
        items: SocialAccountCollaborationItem[],
        last_fetched_at: number,
        loading: boolean,
        error: string | null,
    }>,
    mediaLoading: boolean,
    mediaError: string | null
}

export interface SocialPosts {
    items: SocialPost[],
    last_fetched_at: number,
    expired: boolean,
    page: number,
    has_more: boolean,
    count: number,
    last_fetched_at_server: string
}

export interface Posts {
    posts: Post[],
    items: Post[]
    count:number,
    last_fetched_at: string
}

export interface InfluencerInsights {
    insights: Insights,
    averages: Averages
}


export interface DemographicInsightsItem {
    key: string,
    value: string
}

export enum DemographicCategory {
    FOLLOWER = "follower",
    ENGAGED_AUDIENCE = "engaged_audience",
    REACHED_AUDIENCE = "reached_audience"
}

export enum DemographicType {
    CITY = "city",
    COUNTRY = "country",
    AGE_GROUP = "age",
    GENDER = "gender"
}

export enum DemographicPeriod {
    MONTH = "month",
    WEEK = "week"
}

export type DemographicInsights = {
    [K in DemographicCategory]: {
        [T in DemographicType]: {
            [P in DemographicPeriod]: DemographicInsightsItem[]
        }
    }
}


export interface Post {
    id: string;
    thumbnail_url: string;
    media_product_type: 'STORY' | 'FEED' | 'REELS';
    like_count: number;
    comments_count: number;
    impressions_count?: number;
    engagement_score: any;
    shares_count?: number;
    date: string;
    month: string;
    views_count?: number;
    reach_count?: number,
    insights?: Insights;
    caption?: string | null;
    posted_on?: string,
    timestamp?: number,
    media_type: 'VIDEO' | 'IMAGE' | 'CAROUSEL_ALBUM',
    media_url: string,
    bookmarked?: "y" | "n"
}

export enum MediaProductType {
    STORY = 'STORY',
    REEL = 'REELS',
    FEED = 'FEED',
    VIDEO = 'VIDEO',
}
